/**
 * Platform + capability detection. Pure functions, no state, no side effects.
 *
 * These are the single source of truth for "where is the user running this?"
 * and drive both the push permission modal variant selection AND the Profile
 * → Settings notifications row's status text.
 *
 * Why UA sniffing for iOS/Android? There's no feature-only way to distinguish
 * iOS WebKit from desktop Safari that's accurate enough to be useful for the
 * "you need to install to get push" decision. UA is imperfect (vendors lie,
 * desktop-class iPad reports as Mac, etc.) but for the modal-variant case
 * the worst outcome is showing the wrong copy — push itself still depends
 * on actual capability checks (hasWebPushSupport, isStandalonePWA).
 */

export type PushPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

/**
 * iOS detection. Matches iPhone, iPad (when in compact mode), and iPod.
 *
 * Modern iPadOS in "desktop site" mode reports as MacIntel and CANNOT be
 * distinguished from a real Mac via UA alone. We pair this with a touch-
 * point check to catch that case — Macs typically don't expose touch.
 * Imperfect but adequate for "show install-instructions modal" gating.
 */
export function isIOS(): boolean {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  // iPadOS desktop-mode detection
  if (
    typeof navigator !== 'undefined' &&
    navigator.platform === 'MacIntel' &&
    (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints !== undefined &&
    ((navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints ?? 0) > 1
  ) {
    return true;
  }
  return false;
}

export function isAndroid(): boolean {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return /Android/i.test(ua);
}

/**
 * Is the user in the browser our install steps are written for — Safari on
 * iOS, Chrome on Android? Add-to-Home-Screen only works reliably there, and
 * the step copy references that browser's exact UI. When this is false, the
 * install modal prepends a "open in Safari/Chrome first" notice.
 *
 * UA sniffing — imperfect but adequate for a help notice:
 *   - iOS: third-party browsers self-tag (CriOS=Chrome, FxiOS=Firefox,
 *     EdgiOS=Edge, OPiOS=Opera, GSA=Google app); anything else is Safari
 *     WebKit.
 *   - Android: "real Chrome" only — the other Chromium skins (Samsung, Edge,
 *     Opera) carry their own menus, and Firefox isn't Chromium, so the
 *     Chrome-specific ⋮ steps don't match any of them.
 */
export function isPreferredInstallBrowser(platform: 'ios' | 'android'): boolean {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (platform === 'ios') {
    return !/CriOS|FxiOS|EdgiOS|OPiOS|GSA/i.test(ua);
  }
  return /Chrome/i.test(ua) && !/SamsungBrowser|EdgA|OPR|Firefox/i.test(ua);
}

/**
 * Are we running as an installed PWA in standalone mode?
 *
 * `display-mode: standalone` covers iOS and Android Chromium. iOS Safari
 * also exposes the legacy `navigator.standalone` flag — we check both for
 * belt-and-suspenders, since older WebKit versions are inconsistent about
 * the matchMedia path.
 */
export function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS-specific legacy flag
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

/**
 * Does the current environment have all three primitives needed for
 * Web Push (Notification API, Service Worker, Push Manager)?
 *
 * iOS Safari in a regular tab returns false here — that's exactly what
 * triggers the "Add to Home Screen" install-instructions modal variant
 * downstream.
 */
export function hasWebPushSupport(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/**
 * Current push permission state. Returns 'unsupported' when the Notification
 * API isn't available (iOS Safari in a tab), so callers can branch without
 * a separate hasWebPushSupport() probe.
 */
export function getPushPermissionState(): PushPermissionState {
  if (!hasWebPushSupport()) return 'unsupported';
  const perm = (window as Window & { Notification?: { permission: NotificationPermission } })
    .Notification?.permission;
  if (perm === 'granted') return 'granted';
  if (perm === 'denied') return 'denied';
  return 'default';
}
